import { useAdminStore } from '../domains/admin';
import { useCommercialStore } from '../domains/commercial';
import { endPlay } from '../domains/end-play';
/**
 * Connection Reset Helper
 * The full reset: every game store and auth back to how a session starts,
 * after the player acknowledges Connection lost, or as they leave the game.
 * A store it does not put back is named in design-checks.mjs's
 * OUTLIVE_A_SESSION, with why (store-left-by-the-full-reset).
 * What a drop ends at once — voice chat, and what play had under way — is the
 * connection-lost domain's handleDropped, which the socket service calls.
 */

import { useVoiceChatStore } from '../domains/voice-chat';
import { useConnectionLostStore } from '../domains/connection-lost';
import { useMatchStore } from '../domains/match';
import { useAirportsStore } from '../domains/airports';
import { useFleetsStore } from '../domains/fleets';
import { useTeamsStore } from '../domains/teams';
import { usePlayersStore } from '../domains/players';
import { useSegmentsStore } from '../domains/segments';
import { useCityAccessStore } from '../domains/city-access';
import { useFinancialsStore } from '../domains/financials';
import { useActivityLogStore } from '../domains/activity-log';
import { useSectionPresenceStore } from '../domains/section-presence';
import { useSectionCursorStore } from '../domains/section-cursor';
import { useCosignStore } from '../domains/cosign';
import { useDialogueStore } from '../domains/dialogue';
import { useTeamActivityStore } from '../domains/team-activity';
import { useMatchPhaseStore } from '../domains/match-phase';
import { useMatchSessionScriptStore } from '../domains/match-session-script';
import { useCapitalStore } from '../domains/capital';
import { useFinancialReportsStore } from '../domains/financials-quarterly';
import { useMissionsStore } from '../domains/missions';
import { useAchievementsStore } from '../domains/achievements';
import { useAirportFlightsStore } from '../domains/itineraries';
import { useEmojiStore } from '../domains/emoji';
import { useRouteShareStore } from '../domains/route-share';
import { useStrengthReportsStore } from '../domains/strength';
import { usePolicyToggleStore } from '../domains/policy-toggles';
import { useInnovationStore } from '../domains/innovation';
import { useGlobePingStore } from '../domains/globe-ping';
import { useHelpRequestStore } from '../domains/help-request';
import { useLocalSavesStore } from '../domains/local-saves';
import { useDialogueLinesStore } from '../domains/dialogue-lines';
import { useServerOperationsStore } from '../domains/server-operations';
import { clearGhostCursor } from '../domains/ghost-cursor';
import { handleSessionClosed } from '../domains/auth';

/**
 * Reset all game stores
 * Called after user acknowledges the connection lost notification
 * This will cause GameScreen to unmount and return to LoginScreen
 * This function is idempotent - safe to call multiple times
 */
export function resetAllGameStores(): void {
  console.log('[ConnectionReset] Resetting all game stores...');

  // What play had under way ends with the match, as it does with a drop
  // (endPlay): a moderator's call, its mute given back, and the command on
  // its way, the gate open and its sender's wait over.
  endPlay();

  // ── Core game data ──────────────────────────────────────────────────────────
  useMatchStore.getState().reset();
  useAirportsStore.getState().reset();
  useFleetsStore.getState().reset();
  useTeamsStore.getState().reset();
  usePlayersStore.getState().reset();
  useSegmentsStore.getState().reset();
  useCityAccessStore.getState().reset();
  useCommercialStore.getState().reset();
  useFinancialsStore.getState().reset();
  useActivityLogStore.getState().reset();

  // ── Session script / phase / activity ─────────────────────────────────────
  // These must be cleared so a new match doesn't inherit stale phase/activity state
  useMatchPhaseStore.getState().reset();
  useMatchSessionScriptStore.getState().reset();
  useTeamActivityStore.getState().reset();
  useDialogueStore.getState().reset();

  // ── Dialogue lines (audio cache) ──────────────────────────────────────────
  useDialogueLinesStore.getState().reset();

  // ── Financial reports & market data ───────────────────────────────────────
  useFinancialReportsStore.getState().reset();
  useCapitalStore.getState().reset();
  useStrengthReportsStore.getState().reset();

  // ── Team tools & features ─────────────────────────────────────────────────
  useMissionsStore.getState().reset();
  // A celebration still queued as the player leaves is not shown in their next match.
  useAchievementsStore.getState().reset();
  usePolicyToggleStore.getState().reset();
  useInnovationStore.getState().reset();
  useRouteShareStore.getState().reset();
  // A help request is held by the match's server: the next match starts with none.
  useHelpRequestStore.getState().reset();
  // An admin action on its way went with the match, and so did a save of it.
  useAdminStore.getState().reset();
  useLocalSavesStore.getState().reset();

  // ── UI / real-time overlays ───────────────────────────────────────────────
  useAirportFlightsStore.getState().reset();
  useEmojiStore.getState().reset();
  useGlobePingStore.getState().reset();
  useSectionPresenceStore.getState().reset();
  useSectionCursorStore.getState().reset();
  useCosignStore.getState().reset();
  // The performance panel lists the server's operations since the player
  // joined: the next match starts with none.
  useServerOperationsStore.getState().reset();
  // The developer's ghost cursor is pinned on a button of this match's: it
  // goes with the match, its hover's leave going nowhere with the socket gone.
  clearGhostCursor();

  // ── Voice chat ─────────────────────────────────────────────────────────────
  // reset() puts voice back as a fresh page has it — the player's own mute as
  // they last chose it (a call's, given back above), the session's switches
  // cleared, and no participant token/room, so LiveKit doesn't reuse the
  // previous session's credentials on the next join.
  useVoiceChatStore.getState().reset();

  // ── Connection ─────────────────────────────────────────────────────────────
  // A ban's bar on reconnecting ends with the session it ended, so the next
  // one in this tab reconnects as usual.
  useConnectionLostStore.getState().reset();

  console.log('[ConnectionReset] All domain stores reset');

  // ── Auth (triggers GameScreen unmount) ────────────────────────────────────
  handleSessionClosed();
  console.log('[ConnectionReset] Auth store reset — GameScreen will unmount');

  console.log('[ConnectionReset] All stores reset successfully');
}
